import React from 'react';
import { Button, Card, Col, Descriptions, Row, Space, Statistic, Tag } from 'antd';
import { AccountBookOutlined, BarChartOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { epcService } from '@/services/epc.service';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

type Props = { projectId?: string };

export const ConstructionDashboardPanel: React.FC<Props> = ({ projectId }) => {
  const q = useQuery({
    queryKey: ['cep-dashboards', projectId],
    queryFn: () => epcService.getCepDashboards(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const costQ = useQuery({
    queryKey: ['cep-dashboards-cost', projectId],
    queryFn: () => projectService.getCostControlSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  if (!projectId) return <Tag>Select a project for the construction dashboard</Tag>;

  const d = (q.data || {}) as any;
  const pkgs = d.packages || {};
  const byStatus = pkgs.by_status || {};
  const cost = (costQ.data || {}) as Record<string, unknown>;
  const costActive = !!cost.active;

  return (
    <div>
      <Tag color="geekblue" style={{ marginBottom: 12 }}>
        CEP Wave 5 — operational overview (advisory only)
      </Tag>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" loading={q.isLoading}>
            <Statistic title="Packages" value={pkgs.total ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={q.isLoading}>
            <Statistic
              title="Project progress %"
              value={d.progress?.project_progress_pct ?? 0}
              precision={1}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={q.isLoading}>
            <Statistic title="Open constraints" value={d.constraints?.open ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={q.isLoading}>
            <Statistic title="Open delays" value={d.delays?.open ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Project Cost Control"
        style={{ marginBottom: 16 }}
        loading={costQ.isLoading}
        extra={
          <Space size="small">
            <Link to={`/prj/projects/${projectId}/budgets`}>
              <Button size="small" icon={<AccountBookOutlined />}>
                Budget
              </Button>
            </Link>
            <Link to={`/prj/projects/${projectId}/budgets/variance`}>
              <Button size="small" type="primary" icon={<BarChartOutlined />}>
                BVA
              </Button>
            </Link>
          </Space>
        }
      >
        {!costActive ? (
          <Tag>Cost control inactive — approve a Project Cost Budget</Tag>
        ) : (
          <Row gutter={[12, 8]}>
            <Col xs={12} md={6}>
              <Statistic
                title="Budget"
                value={Number(cost.budgeted) || 0}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Committed"
                value={Number(cost.committed) || 0}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Actual"
                value={Number(cost.actual) || 0}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title="Available"
                value={Number(cost.available) || 0}
                valueStyle={Number(cost.available) < 0 ? { color: '#cf1322' } : undefined}
                formatter={(v) => formatMoney(Number(v))}
              />
            </Col>
          </Row>
        )}
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Card size="small" title="Package status mix" loading={q.isLoading}>
            <Descriptions size="small" column={1}>
              {Object.entries(byStatus).map(([k, v]) => (
                <Descriptions.Item key={k} label={k}>
                  {String(v)}
                </Descriptions.Item>
              ))}
              {!Object.keys(byStatus).length && <Tag>No packages yet</Tag>}
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Risk & readiness" loading={q.isLoading}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Elevated constraints">
                {d.constraints?.elevated ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="EoT candidates">{d.delays?.eot_candidates ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Delay hours (open)">
                {d.delays?.open_hours ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Packages waiting permit">
                {d.permits?.packages_waiting ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Weather stops (24h)">
                {d.weather?.work_stopped_last_24h ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Actual hours">
                {d.productivity?.actual_hours ?? 0}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ConstructionDashboardPanel;
